import { gql } from '@urql/core';
import { SALES_CHANNEL_FRAGMENT } from './settings';

export const GET_PRODUCTS = gql`
  query GetProducts($options: ProductListOptions) {
    products(options: $options) {
      items {
        id
        name
        slug
        createdAt
        enabled
        featuredAsset {
          id
          preview
        }
        collections {
          id
          name
        }
        facetValues {
          id
          name
          facet {
            id
            name
          }
        }
        variantList(options: { take: 100 }) {
          items {
            id
            name
            sku
            customFields {
              barcode
            }
            price
            isOnSale
            salePrice
            originalPrice
            discountPercentage
            saleEndsAt
            activeCatalogDiscountId
            stockOnHand
            stockAllocated
            trackInventory
            outOfStockThreshold
            useGlobalOutOfStockThreshold
            stockLevels {
              stockLocationId
              stockOnHand
              stockAllocated
            }
            prices {
              price
              currencyCode
              customFields {
                compareAtPrice
                costPrice
              }
            }
          }
          totalItems
        }
      }
      totalItems
    }
  }
`;

export const GET_PRODUCTS_FOR_BULK_EDIT = gql`
  query GetProductsForBulkEdit($options: ProductListOptions) {
    products(options: $options) {
      items {
        id
        name
        featuredAsset {
          id
          preview
        }
        variantList(options: { take: 1000, sort: { createdAt: ASC, id: ASC } }) {
          items {
            id
            name
            sku
            customFields {
              barcode
            }
            price
            isOnSale
            salePrice
            originalPrice
            discountPercentage
            saleEndsAt
            activeCatalogDiscountId
            enabled
            currencyCode
            trackInventory
            outOfStockThreshold
            useGlobalOutOfStockThreshold
            prices {
              price
              currencyCode
              customFields {
                compareAtPrice
              }
            }
            stockLevels {
              id
              updatedAt
              stockLocationId
              stockOnHand
              stockAllocated
            }
          }
          totalItems
        }
      }
      totalItems
    }
  }
`;

export const GET_BULK_EDIT_SELECTED_PRODUCT_IDS = gql`
  query GetBulkEditSelectedProductIds($options: ProductListOptions) {
    products(options: $options) {
      items { id }
      totalItems
    }
  }
`;

// Deliberately minimal list operations for the bulk editor. The page walks
// these lists in bounded pages and fails closed if the list changes mid-read.
export const GET_BULK_EDIT_BRANDS = gql`
  query GetBulkEditBrands($options: BrandListOptions) {
    brands(options: $options) {
      items { id name }
      totalItems
    }
  }
`;

export const GET_BULK_EDIT_COLLECTIONS = gql`
  query GetBulkEditCollections($options: CollectionListOptions) {
    collections(options: $options) {
      items { id name }
      totalItems
    }
  }
`;

export const GET_BULK_EDIT_COLLECTION_PRODUCT_IDS = gql`
  query GetBulkEditCollectionProductIds($id: ID!, $options: ProductVariantListOptions) {
    collection(id: $id) {
      id
      productVariants(options: $options) {
        items { id product { id } }
        totalItems
      }
    }
  }
`;

export const BULK_UPDATE_CATALOG_VARIANT = gql`
  mutation BulkUpdateCatalogVariant($input: BulkCatalogVariantUpdateInput!) {
    bulkUpdateCatalogVariant(input: $input) {
      success
      variantId
      adjustedCount
      conflicts {
        code
        productVariantId
        stockLocationId
        intendedStockOnHand
        expectedKind
        expectedStockLevelId
        expectedUpdatedAt
        expectedStockOnHand
        currentStockLevelId
        currentUpdatedAt
        currentStockOnHand
      }
    }
  }
`;

export const GET_PRODUCT = gql`
  query GetProduct($id: ID!) {
    product(id: $id) {
      id
      name
      slug
      description
      enabled
      featuredAsset {
        id
        preview
      }
      assets {
        id
        preview
      }
      variants {
        id
        name
        translations {
          languageCode
          name
        }
        sku
        price
        isOnSale
        salePrice
        originalPrice
        discountPercentage
        saleEndsAt
        activeCatalogDiscountId
        currencyCode
        prices {
          price
          currencyCode
          customFields {
            compareAtPrice
            costPrice
            costPriceSource
          }
        }
        stockOnHand
        trackInventory
        outOfStockThreshold
        useGlobalOutOfStockThreshold
        stockLevels {
          stockLocationId
          stockOnHand
          stockAllocated
        }
        taxCategory {
          id
          name
        }
        options {
          id
          name
          code
          translations {
            languageCode
            name
          }
        }
        customFields {
          barcode
          length
          width
          height
          weight
          weightUnit
          minimumOrderQuantity
          isBundle
        }
      }
      optionGroups {
        id
        name
        code
        translations {
          languageCode
          name
        }
        options {
          id
          name
          code
          translations {
            languageCode
            name
          }
        }
      }
      facetValues {
        id
        name
        code
        facet {
          id
          name
          code
          customFields {
            specType
            unit
            displayMode
            propertyUsage
          }
        }
      }
      channels {
        id
        code
      }
      categories {
        id
        name
        slug
      }
      categoryAssignments {
        categoryId
        productId
        position
        isPrimary
        isPinned
        pinPosition
        category {
          id
          name
          slug
        }
      }
      collections {
        id
        name
        slug
        breadcrumbs {
          id
          name
          slug
        }
        customFields {
          collectionType
        }
      }
      customFields {
        metaTitle
        metaDescription
        brandId
        primaryCategoryId
        maxOrderQuantity
      }
      labels {
        code
        name
        colorPreset
        type
      }
      merchantLabels {
        id
        code
        name
        colorPreset
      }
      translations {
        id
        languageCode
        name
        slug
        description
        customFields {
          metaTitle
          metaDescription
        }
      }
    }
  }
`;

export const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
      slug
      description
      enabled
      featuredAsset {
        id
        preview
      }
    }
  }
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
      name
      slug
      description
      enabled
      featuredAsset {
        id
        preview
      }
    }
  }
`;

export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    deleteProduct(id: $id) {
      result
      message
    }
  }
`;

export const DELETE_PRODUCTS = gql`
  mutation DeleteProducts($ids: [ID!]!) {
    deleteProducts(ids: $ids) {
      result
      message
    }
  }
`;

export const GET_TRASHED_PRODUCTS = gql`
  query GetTrashedProducts($options: TrashedProductListOptions) {
    trashedProducts(options: $options) {
      items {
        id
        name
        slug
        enabled
        deletedAt
        variantCount
        restorable
        featuredAsset {
          id
          preview
        }
      }
      totalItems
    }
  }
`;

export const GET_TRASHED_PRODUCT_COUNT = gql`
  query GetTrashedProductCount {
    trashedProductCount
  }
`;

export const RESTORE_PRODUCT = gql`
  mutation RestoreProduct($id: ID!) {
    restoreProduct(id: $id) {
      id
      name
      enabled
    }
  }
`;

export const RESTORE_PRODUCTS = gql`
  mutation RestoreProducts($ids: [ID!]!) {
    restoreProducts(ids: $ids) {
      id
      name
      enabled
    }
  }
`;

export const PERMANENTLY_DELETE_PRODUCT = gql`
  mutation PermanentlyDeleteProduct($id: ID!) {
    permanentlyDeleteProduct(id: $id) {
      result
      message
    }
  }
`;

export const PERMANENTLY_DELETE_PRODUCTS = gql`
  mutation PermanentlyDeleteProducts($ids: [ID!]!) {
    permanentlyDeleteProducts(ids: $ids) {
      result
      message
    }
  }
`;

export const CREATE_PRODUCT_VARIANTS = gql`
  mutation CreateProductVariants($input: [CreateProductVariantInput!]!) {
    createProductVariants(input: $input) {
      id
      name
      sku
      price
      stockOnHand
      trackInventory
      stockLevels {
        stockLocationId
        stockOnHand
        stockAllocated
      }
      taxCategory {
        id
        name
      }
      options {
        id
        name
        code
      }
      customFields {
        barcode
        length
        width
        height
        weight
        weightUnit
        minimumOrderQuantity
      }
    }
  }
`;

/**
 * Assign newly-created ProductVariants to the tenant's Channel.
 *
 * commerce kernel's `createProductVariants` does NOT reliably assign new variants to
 * the request's active channel — they end up only in the default channel.
 * Shop-api (tenant-channel-scoped) then can't see them. Call this after every
 * `createProductVariants` with the tenant's Channel ID.
 */
export const ASSIGN_PRODUCT_VARIANTS_TO_CHANNEL = gql`
  mutation AssignProductVariantsToChannel($input: AssignProductVariantsToChannelInput!) {
    assignProductVariantsToChannel(input: $input) {
      id
      channels {
        id
        code
      }
    }
  }
`;

export const UPDATE_PRODUCT_VARIANTS = gql`
  mutation UpdateProductVariants($input: [UpdateProductVariantInput!]!) {
    updateProductVariants(input: $input) {
      id
      name
      sku
      price
      stockOnHand
      trackInventory
      stockLevels {
        stockLocationId
        stockOnHand
        stockAllocated
      }
      taxCategory {
        id
        name
      }
      options {
        id
        name
        code
      }
      customFields {
        barcode
        length
        width
        height
        weight
        weightUnit
        minimumOrderQuantity
        isBundle
      }
    }
  }
`;

export const DELETE_PRODUCT_VARIANT = gql`
  mutation DeleteProductVariant($id: ID!) {
    deleteProductVariant(id: $id) {
      result
      message
    }
  }
`;

export const REMOVE_VARIANT_FROM_LOCATIONS = gql`
  mutation RemoveVariantFromLocations($input: [RemoveVariantFromLocationInput!]!) {
    removeVariantFromLocations(input: $input) {
      success
      removedCount
    }
  }
`;

export const GET_PRODUCT_OPTION_GROUPS = gql`
  query GetProductOptionGroups($options: ProductOptionGroupListOptions) {
    productOptionGroups(options: $options) {
      items {
        id
        name
        code
        translations {
          languageCode
          name
        }
        options {
          id
          name
          code
          translations {
            languageCode
            name
          }
        }
      }
      totalItems
    }
  }
`;

export const GET_GLOBAL_OPTION_GROUPS = gql`
  query GetGlobalOptionGroups {
    globalOptionGroups {
      items {
        id
        name
        code
        displayType
        translations {
          languageCode
          name
        }
        values {
          id
          name
          code
          hexColor
          channelId
          translations {
            languageCode
            name
          }
          swatchImage {
            id
            url
            preview
          }
        }
      }
      totalItems
    }
  }
`;

export const ADD_MERCHANT_OPTION_VALUE = gql`
  mutation AddMerchantOptionValue($input: AddMerchantOptionValueInput!) {
    addMerchantOptionValue(input: $input) {
      id
      code
      name
      hexColor
      channelId
      groupId
      translations {
        languageCode
        name
      }
    }
  }
`;

export const DELETE_MERCHANT_OPTION_VALUE = gql`
  mutation DeleteMerchantOptionValue($id: ID!) {
    deleteMerchantOptionValue(id: $id) {
      result
      message
    }
  }
`;

export const CREATE_PRODUCT_OPTION_GROUP = gql`
  mutation CreateProductOptionGroup($input: CreateProductOptionGroupInput!) {
    createProductOptionGroup(input: $input) {
      id
      name
      code
      options {
        id
        name
        code
      }
    }
  }
`;

export const CREATE_PRODUCT_OPTION = gql`
  mutation CreateProductOption($input: CreateProductOptionInput!) {
    createProductOption(input: $input) {
      id
      name
      code
      groupId
    }
  }
`;

export const SEARCH_PRODUCTS = gql`
  query SearchProducts($input: SearchInput!) {
    search(input: $input) {
      items {
        productId
        productName
        productVariantId
        productVariantName
        sku
        priceWithTax {
          ... on SinglePrice {
            value
          }
          ... on PriceRange {
            min
            max
          }
        }
        productAsset {
          preview
        }
        inStock
      }
      totalItems
    }
  }
`;

/**
 * Draft orders must read the transactional Admin catalog rather than the
 * eventually-consistent search index. A product created moments ago must be
 * immediately available to the merchant, even while the search worker is
 * catching up.
 */
export const GET_DRAFT_ORDER_PICKER_VARIANTS = gql`
  query GetDraftOrderPickerVariants($options: ProductVariantListOptions) {
    productVariants(options: $options) {
      items {
        id
        name
        sku
        enabled
        priceWithTax
        stockOnHand
        stockAllocated
        trackInventory
        outOfStockThreshold
        useGlobalOutOfStockThreshold
        stockLevels {
          stockLocationId
          stockOnHand
          stockAllocated
        }
        options {
          id
          name
          code
          group {
            name
          }
        }
        product {
          id
          name
          featuredAsset {
            preview
          }
          variants {
            id
            enabled
          }
        }
      }
      totalItems
    }
  }
`;

export const GET_DRAFT_ORDER_COLLECTION_PICKER_VARIANTS = gql`
  query GetDraftOrderCollectionPickerVariants(
    $collectionId: ID!
    $options: ProductVariantListOptions
  ) {
    collection(id: $collectionId) {
      id
      productVariants(options: $options) {
        items {
          id
          name
          sku
          enabled
          priceWithTax
          stockOnHand
          stockAllocated
          trackInventory
          outOfStockThreshold
          useGlobalOutOfStockThreshold
          stockLevels {
            stockLocationId
            stockOnHand
            stockAllocated
          }
          options {
            id
            name
            code
            group {
              name
            }
          }
          product {
            id
            name
            featuredAsset {
              preview
            }
            variants {
              id
              enabled
            }
          }
        }
        totalItems
      }
    }
  }
`;

export const GET_PRODUCT_VARIANTS_FOR_PICKER = gql`
  query GetProductVariantsForPicker($productId: String!) {
    productVariants(
      options: {
        take: 100
        filter: {
          productId: { eq: $productId }
          enabled: { eq: true }
        }
        sort: { name: ASC }
      }
    ) {
      items {
        id
        name
        sku
        priceWithTax
        stockOnHand
        stockAllocated
        trackInventory
        outOfStockThreshold
        useGlobalOutOfStockThreshold
        stockLevels {
          stockLocationId
          stockOnHand
          stockAllocated
        }
        options {
          id
          name
          code
          group {
            name
          }
        }
      }
    }
  }
`;

export const ADD_OPTION_GROUP_TO_PRODUCT = gql`
  mutation AddOptionGroupToProduct($productId: ID!, $optionGroupId: ID!) {
    addOptionGroupToProduct(productId: $productId, optionGroupId: $optionGroupId) {
      id
      optionGroups {
        id
        name
        code
        options {
          id
          name
          code
        }
      }
    }
  }
`;

export const REMOVE_OPTION_GROUP_FROM_PRODUCT = gql`
  mutation RemoveOptionGroupFromProduct($productId: ID!, $optionGroupId: ID!) {
    removeOptionGroupFromProduct(productId: $productId, optionGroupId: $optionGroupId) {
      ... on Product {
        id
        optionGroups {
          id
          name
          code
        }
      }
      ... on ProductOptionInUseError {
        errorCode
        message
        optionGroupCode
        productVariantCount
      }
    }
  }
`;

export const DELETE_PRODUCT_OPTION = gql`
  mutation DeleteProductOption($id: ID!) {
    deleteProductOption(id: $id) {
      result
      message
    }
  }
`;

export const UPDATE_PRODUCT_FACETS = gql`
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
      facetValues {
        id
        name
        code
        facet {
          id
          name
        }
      }
    }
  }
`;

export const GET_PRODUCT_VARIANTS_WITH_STOCK = gql`
  query GetProductVariantsWithStock($options: ProductVariantListOptions) {
    productVariants(options: $options) {
      items {
        id
        name
        sku
        enabled
        stockOnHand
        stockAllocated
        trackInventory
        outOfStockThreshold
        useGlobalOutOfStockThreshold
        stockLevels {
          stockLocationId
          stockOnHand
          stockAllocated
        }
        options {
          id
          name
          code
        }
        product {
          id
          name
          featuredAsset {
            preview
          }
          customFields {
            brandId
          }
        }
      }
      totalItems
    }
  }
`;

// ==========================================
// COLLECTION FILTERS
// ==========================================

export const GET_PRODUCT_VARIANT_NAMES = gql`
  query GetProductVariantNames($options: ProductVariantListOptions) {
    productVariants(options: $options) {
      items {
        id
        name
      }
    }
  }
`;

export const GET_PRODUCT_SALES_CHANNELS = gql`
  ${SALES_CHANNEL_FRAGMENT}
  query GetProductSalesChannels($productId: ID!) {
    productSalesChannels(productId: $productId) {
      ...SalesChannelFields
    }
  }
`;

export const SET_PRODUCT_SALES_CHANNELS = gql`
  mutation SetProductSalesChannels($productId: ID!, $salesChannelIds: [ID!]!) {
    setProductSalesChannels(productId: $productId, salesChannelIds: $salesChannelIds)
  }
`;

const BRAND_FRAGMENT = gql`
  fragment BrandFields on Brand {
    id
    createdAt
    updatedAt
    slug
    name
    description
    seoTitle
    seoDescription
    isActive
    sortOrder
    logoAsset {
      id
      preview
      source
    }
    translations {
      id
      languageCode
      name
      description
      seoTitle
      seoDescription
    }
  }
`;

export const GET_BRANDS = gql`
  ${BRAND_FRAGMENT}
  query GetBrands($options: BrandListOptions) {
    brands(options: $options) {
      items {
        ...BrandFields
      }
      totalItems
    }
  }
`;

export const GET_BRAND = gql`
  ${BRAND_FRAGMENT}
  query GetBrand($id: ID!) {
    brand(id: $id) {
      ...BrandFields
    }
  }
`;

export const CREATE_BRAND = gql`
  ${BRAND_FRAGMENT}
  mutation CreateBrand($input: CreateBrandInput!) {
    createBrand(input: $input) {
      ...BrandFields
    }
  }
`;

export const UPDATE_BRAND = gql`
  ${BRAND_FRAGMENT}
  mutation UpdateBrand($input: UpdateBrandInput!) {
    updateBrand(input: $input) {
      ...BrandFields
    }
  }
`;

export const DELETE_BRAND = gql`
  mutation DeleteBrand($id: ID!) {
    deleteBrand(id: $id) {
      result
      message
    }
  }
`;

export const SET_PRODUCT_BRAND = gql`
  mutation SetProductBrand($productId: ID!, $brandId: ID) {
    setProductBrand(productId: $productId, brandId: $brandId) {
      success
    }
  }
`;

const PRODUCT_LABEL_FRAGMENT = gql`
  fragment ProductLabelFields on ProductLabel {
    id
    createdAt
    updatedAt
    code
    colorPreset
    priority
    isActive
    name
    translations {
      id
      languageCode
      name
    }
  }
`;

export const GET_PRODUCT_LABELS = gql`
  ${PRODUCT_LABEL_FRAGMENT}
  query GetProductLabels($options: ProductLabelListOptions) {
    productLabels(options: $options) {
      items {
        ...ProductLabelFields
      }
      totalItems
    }
  }
`;

export const GET_PRODUCT_LABEL = gql`
  ${PRODUCT_LABEL_FRAGMENT}
  query GetProductLabel($id: ID!) {
    productLabel(id: $id) {
      ...ProductLabelFields
    }
  }
`;

export const CREATE_PRODUCT_LABEL = gql`
  ${PRODUCT_LABEL_FRAGMENT}
  mutation CreateProductLabel($input: CreateProductLabelInput!) {
    createProductLabel(input: $input) {
      ...ProductLabelFields
    }
  }
`;

export const UPDATE_PRODUCT_LABEL = gql`
  ${PRODUCT_LABEL_FRAGMENT}
  mutation UpdateProductLabel($input: UpdateProductLabelInput!) {
    updateProductLabel(input: $input) {
      ...ProductLabelFields
    }
  }
`;

export const DELETE_PRODUCT_LABEL = gql`
  mutation DeleteProductLabel($id: ID!) {
    deleteProductLabel(id: $id) {
      result
      message
    }
  }
`;

export const ASSIGN_LABELS_TO_PRODUCT = gql`
  mutation AssignLabelsToProduct($productId: ID!, $labelIds: [ID!]!) {
    assignLabelsToProduct(productId: $productId, labelIds: $labelIds)
  }
`;

export const REMOVE_LABELS_FROM_PRODUCT = gql`
  mutation RemoveLabelsFromProduct($productId: ID!, $labelIds: [ID!]!) {
    removeLabelsFromProduct(productId: $productId, labelIds: $labelIds)
  }
`;

export const GET_SYSTEM_LABEL_SETTINGS = gql`
  query GetSystemLabelSettings {
    systemLabelSettings {
      id
      enabled
      config
      definition {
        id
        code
        ruleType
        defaultConfig
        colorPreset
        defaultPriority
        defaultEnabled
        name
        translations {
          id
          languageCode
          name
        }
      }
    }
  }
`;

export const UPDATE_SYSTEM_LABEL_SETTING = gql`
  mutation UpdateSystemLabelSetting($input: UpdateTenantLabelSettingInput!) {
    updateSystemLabelSetting(input: $input) {
      id
      enabled
      config
    }
  }
`;

const PRODUCT_CUSTOMIZATION_FIELD_FRAGMENT = gql`
  fragment ProductCustomizationFieldFields on ProductCustomizationField {
    id
    productId
    fieldType
    price
    isRequired
    isPublished
    displayOrder
    configs
    label
    hint
    translations {
      id
      languageCode
      label
      hint
    }
    options {
      id
      price
      position
      name
      translations {
        id
        languageCode
        name
      }
    }
  }
`;

export const GET_PRODUCT_CUSTOMIZATION_FIELDS = gql`
  query GetProductCustomizationFields($productId: ID!) {
    productCustomizationFields(productId: $productId) {
      ...ProductCustomizationFieldFields
    }
  }
  ${PRODUCT_CUSTOMIZATION_FIELD_FRAGMENT}
`;

export const CREATE_PRODUCT_CUSTOMIZATION_FIELD = gql`
  mutation CreateProductCustomizationField($input: CreateProductCustomizationFieldInput!) {
    createProductCustomizationField(input: $input) {
      ...ProductCustomizationFieldFields
    }
  }
  ${PRODUCT_CUSTOMIZATION_FIELD_FRAGMENT}
`;

export const UPDATE_PRODUCT_CUSTOMIZATION_FIELD = gql`
  mutation UpdateProductCustomizationField($input: UpdateProductCustomizationFieldInput!) {
    updateProductCustomizationField(input: $input) {
      ...ProductCustomizationFieldFields
    }
  }
  ${PRODUCT_CUSTOMIZATION_FIELD_FRAGMENT}
`;

export const DELETE_PRODUCT_CUSTOMIZATION_FIELD = gql`
  mutation DeleteProductCustomizationField($id: ID!) {
    deleteProductCustomizationField(id: $id) {
      result
      message
    }
  }
`;

export const COPY_PRODUCT_CUSTOMIZATIONS = gql`
  mutation CopyProductCustomizations(
    $sourceProductId: ID!
    $targetProductId: ID!
    $mode: ProductCustomizationCopyMode
  ) {
    copyProductCustomizations(
      sourceProductId: $sourceProductId
      targetProductId: $targetProductId
      mode: $mode
    ) {
      ...ProductCustomizationFieldFields
    }
  }
  ${PRODUCT_CUSTOMIZATION_FIELD_FRAGMENT}
`;

// GET_PRODUCT_ASSOCIATIONS moved to ./operations/associations.ts (single source of truth).
// Duplicate definition removed to fix the Vite "conflicting star exports" error.

export const SET_PRODUCT_ASSOCIATIONS = gql`
  mutation SetProductAssociations($input: SetProductAssociationsInput!) {
    setProductAssociations(input: $input) {
      id
      productId
      associatedProductId
      typeCode
      position
      source
      associatedProduct {
        id
        name
        slug
        featuredAsset {
          id
          preview
        }
      }
    }
  }
`;

export const REMOVE_PRODUCT_ASSOCIATION = gql`
  mutation RemoveProductAssociation($id: ID!) {
    removeProductAssociation(id: $id) {
      result
      message
    }
  }
`;

// Transactional product upsert (backend @tali/catalog/product-save). Replaces the
// legacy multi-mutation save: product + option groups/options + variants
// (prices/stock) + Brand + Category + additional-field state in one transaction.
export const SAVE_PRODUCT = gql`
  mutation SaveProduct($input: SaveProductInput!) {
    saveProduct(input: $input) {
      product {
        id
        name
        enabled
        variants {
          id
          sku
        }
        optionGroups {
          code
        }
      }
    }
  }
`;

export const GET_PRODUCT_TAX_PREVIEW = gql`
  query ProductTaxPreview($input: ProductTaxPreviewInput!) {
    productTaxPreview(input: $input) {
      price
      priceWithTax
      taxRate
      registrationStatus
      pricesIncludeTax
      currencyCode
    }
  }
`;

/** One request for every status tab's count; `take: 1` keeps each alias cheap. */
export const GET_PRODUCT_TAB_COUNTS = gql`
  query GetProductTabCounts($all: ProductListOptions, $active: ProductListOptions, $inactive: ProductListOptions) {
    all: products(options: $all) { totalItems }
    active: products(options: $active) { totalItems }
    inactive: products(options: $inactive) { totalItems }
  }
`;
